import { GetServerSidePropsContext } from 'next';
import { getServerSession } from 'next-auth';
import { signIn } from 'next-auth/react';
import { useEffect } from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { nextAuthOptions } from '../helpers/nextAuthOptions';
import { getTLD } from '../helpers/domain';
import { PAGE } from '../helpers/pages';
import { userRepository } from '../repositories/User/UserRepository';
import { isOk } from '../domains/Result';
import { formatAuthorizationHeader } from '../helpers/auth';
import formatCallbackURL from '../utils/formatCallbackURL';
import { formatCookie, getDomainFromBaseURL } from '../utils/CookieUtils';
import { COOKIE, MAXIMUM_COOKIE_AGE } from '../helpers/constants';
import MetricService from '../services/MetricService';
import axios from 'axios';
import { logger } from '@dist-marketplace-frontend/services';

type Props = {
  callbackUrl: string;
  authenticated: boolean;
  error: boolean;
};

export default function SignInPage(props: Props) {
  const { callbackUrl, authenticated, error } = props;
  const { push } = useRouter();

  useEffect(() => {
    if (error) {
      push('/');
    } else if (!authenticated) {
      signIn('keycloak', { callbackUrl });
    } else {
      /**
       * We use native methods of updating the URL, since those methods associated with Next Router,
       * such as push and replace, may lead to the page failing to load if the page uses a rewrite.
       * This includes pages such as myads on the dashboard, whose URL is rewritten from "listings" to "myads".
       */
      window.location.href = callbackUrl;
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, error]);

  if (error) return <div>Unable to log in</div>;

  if (!authenticated) return <div>Redirecting to login...</div>;

  return <div>Redirecting...</div>;
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const req = context.req;
  const res = context.res;
  const query = context.query;
  const tld = getTLD(req) as string;
  const {
    publicRuntimeConfig: { BASE_URL },
  } = getConfig();
  const baseUrl = `${BASE_URL}${tld}`;
  const session = await getServerSession(req, res, nextAuthOptions);

  const { callbackUrl: unformattedCallbackURL, ...rest } = query;

  /**
   * Next auth only handles on query in the callback URL. In order to retain all the query
   * parameters applied on signin, we must do some formatting with the query object.
   * */
  const callbackUrl = formatCallbackURL({
    unformattedCallbackURL,
    queries: rest,
  });

  if (query.error) {
    logger.error({
      route: PAGE.SIGNIN,
      message: `Received error query parameter, equal to: ${query.error.toString()}`,
    });

    return {
      props: {
        error: true,
      },
    };
  }

  if (session && !session.error && session.accessToken) {
    const cookies: string[] = [];
    try {
      const response = await userRepository.get(
        formatAuthorizationHeader(session.accessToken),
      );
      if (isOk(response)) {
        const userID = response.ok.user.id.toString();
        const sessionUID = formatCookie(COOKIE.DD_CLIENT_UID, userID, {
          domain: getDomainFromBaseURL(baseUrl),
          maxAge: MAXIMUM_COOKIE_AGE,
          httpOnly: false,
        });
        cookies.push(sessionUID);
        res?.setHeader('set-cookie', cookies);
        return {
          props: {
            callbackUrl: callbackUrl || baseUrl,
            authenticated: true,
          },
        };
      } else {
        logger.error({
          route: PAGE.SIGNIN,
          message: 'Error retrieving user with session',
          error: response.error,
        });
        return {
          props: {
            error: true,
          },
        };
      }
    } catch (error) {
      logger.error({
        route: PAGE.SIGNIN,
        message: 'There was an error getting the legacy session for session',
        error,
      });
      if (axios.isAxiosError(error)) {
        const metricService = MetricService.get();
        metricService.incrementRequestErrorCount('signin', {
          statusCode: error.response?.status,
        });
      }
      return {
        props: {
          error: true,
        },
      };
    }
  }
  /**
   * This is not an error.
   * The user has been redirected here from the authGuard.
   * We pass the callback url to client side signIn so the user continues on their journey
   */
  return {
    props: {
      callbackUrl,
      authenticated: false,
      error: false,
    },
  };
};
